import { useEffect, useState } from "react";
import { CircleAlert, Flame, Loader2, RefreshCw, Snowflake, Target } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { hasMinRole, useAuthStore } from "@/features/auth/store";
import { useGenerateLeadScore } from "@/features/calls/hooks";
import type { Call } from "@/features/calls/api";

export default function LeadScoreCard({
  call,
  hasTranscript,
}: {
  call: Call;
  hasTranscript: boolean;
}) {
  const role = useAuthStore((s) => s.user?.role) ?? "member";
  const canEdit = hasMinRole(role, "manager");
  const gen = useGenerateLeadScore(call.id);

  const status = call.lead_score_status || "pending";
  const score = call.lead_score;
  const grade = call.lead_grade || "";
  const signals = call.buying_signals || [];
  const reasoning = call.lead_reasoning || "";

  const hasScore = score !== null && score !== undefined;
  // "pending" is the default for old rows where no task was ever started.
  // Only "processing" + the local mutation count as in-flight.
  const inFlight = status === "processing" || gen.isPending;

  // A task can die after flipping the row to "processing" (worker restart,
  // unregistered task), leaving the row stuck forever. After a grace period
  // we surface a Retry so the user is never trapped on a spinner.
  const [stale, setStale] = useState(false);
  const [retryNonce, setRetryNonce] = useState(0);
  useEffect(() => {
    if (status !== "processing") {
      setStale(false);
      return;
    }
    setStale(false);
    const t = setTimeout(() => setStale(true), 60_000);
    return () => clearTimeout(t);
  }, [status, retryNonce]);

  async function onRegenerate() {
    setStale(false);
    setRetryNonce((n) => n + 1);
    try {
      await gen.mutateAsync();
      toast.info("Re-scoring this lead — takes a few seconds.");
    } catch {
      toast.error("Failed to start lead scoring");
    }
  }

  // Disabled by tenant
  if (status === "disabled") {
    return null;
  }

  // No transcript + no score → don't render at all (matches summary card behavior)
  if (!hasScore && !hasTranscript && !inFlight) {
    return null;
  }

  // Empty state — no score yet
  if (!hasScore && !inFlight) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Target className="size-4 text-primary" />
            Lead Score
          </CardTitle>
          <CardDescription>
            Rate this call as a sales lead (0–100) using AI.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {canEdit ? (
            <Button onClick={onRegenerate} disabled={gen.isPending}>
              <Target className="size-4" />
              {gen.isPending ? "Starting…" : "Generate lead score"}
            </Button>
          ) : (
            <p className="text-sm text-muted-foreground">
              Ask a manager to generate the lead score.
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Target className="size-4 text-primary" />
              Lead Score
              {inFlight && !stale && (
                <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <Loader2 className="size-3 animate-spin" />
                  Scoring…
                </span>
              )}
            </CardTitle>
            <CardDescription>
              AI-rated sales-lead quality (0–100).
            </CardDescription>
          </div>
          {canEdit && (hasScore || (inFlight && stale)) && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRegenerate}
              disabled={gen.isPending || (status === "processing" && !stale)}
            >
              <RefreshCw className="size-3.5" />
              {inFlight && stale ? "Retry" : "Re-score"}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasScore && (
          <div className="flex items-center gap-3">
            <ScoreDial score={score!} grade={grade} />
            <GradePill grade={grade} />
          </div>
        )}

        {status === "failed" && (
          <div className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
            <CircleAlert className="size-4 shrink-0" />
            <span>Lead scoring failed. Try re-running it.</span>
          </div>
        )}

        {inFlight && stale && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
            <CircleAlert className="size-4 shrink-0" />
            <span>
              This is taking longer than expected — the scoring worker may be
              busy or offline. Use Retry above.
            </span>
          </div>
        )}

        {reasoning && (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {reasoning}
          </p>
        )}

        {signals.length > 0 && (
          <div>
            <h4 className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Buying signals
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {signals.map((s, i) => (
                <span
                  key={i}
                  className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        )}

        {grade === "hot" && call.hot_lead_routed_at && (
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
            {(call.hot_lead_routing_channels || []).length > 0 ? (
              <p className="font-medium text-foreground">
                Routed via{" "}
                {(call.hot_lead_routing_channels || [])
                  .map((c) => (c === "in_app" ? "in-app" : c))
                  .join(", ")}
                <span className="font-normal text-muted-foreground">
                  {" · "}
                  {new Date(call.hot_lead_routed_at).toLocaleString()}
                </span>
              </p>
            ) : (
              <p className="text-muted-foreground">
                Hot-lead routing ran but delivered nothing
                {call.hot_lead_routing_error
                  ? ` — ${call.hot_lead_routing_error}`
                  : "."}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ScoreDial({ score, grade }: { score: number; grade: string }) {
  const color =
    grade === "hot"
      ? "text-rose-600 dark:text-rose-400"
      : grade === "warm"
      ? "text-amber-600 dark:text-amber-400"
      : "text-slate-500 dark:text-slate-400";
  return (
    <div className="flex flex-col">
      <span className={`text-3xl font-bold tabular-nums ${color}`}>
        {score}
      </span>
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
        out of 100
      </span>
    </div>
  );
}

function GradePill({ grade }: { grade: string }) {
  if (grade === "hot") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-rose-50 px-3 py-1 text-sm font-semibold text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
        <Flame className="size-4" />
        Hot lead
      </span>
    );
  }
  if (grade === "warm") {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-sm font-semibold text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
        Warm lead
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-sm font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
      <Snowflake className="size-4" />
      Cold lead
    </span>
  );
}
